import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as api from '../api/client';
import { InstanceSettings, StatusPageInput, StatusPageLayoutInput } from '../types';

export function useStatusPages() {
  return useQuery({ queryKey: ['status-pages'], queryFn: api.getStatusPages });
}

export function useStatusPage(id: number) {
  return useQuery({ queryKey: ['status-page', id], queryFn: () => api.getStatusPage(id), enabled: id > 0 });
}

export function useStatusPagePreview(id: number, enabled = true) {
  return useQuery({ queryKey: ['status-page-preview', id], queryFn: () => api.previewStatusPage(id), enabled: id > 0 && enabled });
}

function invalidate(qc: ReturnType<typeof useQueryClient>, id?: number) {
  qc.invalidateQueries({ queryKey: ['status-pages'] });
  if (id) {
    qc.invalidateQueries({ queryKey: ['status-page', id] });
    qc.invalidateQueries({ queryKey: ['status-page-preview', id] });
    qc.invalidateQueries({ queryKey: ['announcements', id] });
  }
}

export function useCreateStatusPage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: StatusPageInput & { slug: string; title: string }) => api.createStatusPage(data),
    onSuccess: () => invalidate(qc),
  });
}

export function useUpdateStatusPage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: StatusPageInput }) => api.updateStatusPage(id, data),
    onSuccess: (_, v) => invalidate(qc, v.id),
  });
}

export function useDeleteStatusPage() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (id: number) => api.deleteStatusPage(id), onSuccess: () => invalidate(qc) });
}

export function useSetStatusPageLayout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: StatusPageLayoutInput }) => api.setStatusPageLayout(id, data),
    onSuccess: (_, v) => invalidate(qc, v.id),
  });
}

export function useAnnouncements(pageId: number) {
  return useQuery({ queryKey: ['announcements', pageId], queryFn: () => api.getAnnouncements(pageId), enabled: pageId > 0 });
}

export function useCreateAnnouncement(pageId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof api.createAnnouncement>[1]) => api.createAnnouncement(pageId, data),
    onSuccess: () => invalidate(qc, pageId),
  });
}

export function useUpdateAnnouncement(pageId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ postId, data }: { postId: number; data: Parameters<typeof api.updateAnnouncement>[2] }) => api.updateAnnouncement(pageId, postId, data),
    onSuccess: () => invalidate(qc, pageId),
  });
}

export function useDeleteAnnouncement(pageId: number) {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (postId: number) => api.deleteAnnouncement(pageId, postId), onSuccess: () => invalidate(qc, pageId) });
}

export function useSettings() {
  return useQuery({ queryKey: ['settings'], queryFn: api.getSettings });
}

export function useUpdateSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<InstanceSettings>) => api.updateSettings(data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['settings'] }); qc.invalidateQueries({ queryKey: ['status-pages'] }); },
  });
}
